import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {Observable, of} from "rxjs";
import {catchError, map, shareReplay} from "rxjs/operators";
import {environment} from "../../../environments/environment";
import {IWalletTypeInfo} from "../interfaces/wallet-type-info.interface";
import {WalletTypeIdEnum} from "../enums/wallet-type-id.enum";
import {WalletTypeEnum} from "../enums/wallet-type.enum";

@Injectable({providedIn: 'root'})
export class WalletTypesService {

  private readonly fallbackWalletTypes: IWalletTypeInfo[] = Object.keys(WalletTypeIdEnum)
    .filter(key => isNaN(Number(key)) && key in WalletTypeEnum)
    .map(key => ({
      id: WalletTypeIdEnum[key as keyof typeof WalletTypeIdEnum],
      walletType: WalletTypeEnum[key as keyof typeof WalletTypeEnum],
      title: key,
      description: '',
      icon: '',
    }));

  private readonly realFallback: IWalletTypeInfo = {
    id: WalletTypeIdEnum.REAL,
    walletType: WalletTypeEnum.REAL,
    title: 'REAL',
    description: '',
    icon: '',
  };

  private readonly walletTypes$: Observable<IWalletTypeInfo[]> = this.http
    .get<IWalletTypeInfo[]>(`${environment.GATEWAY_URL}/wallet/types`)
    .pipe(
      catchError((error: HttpErrorResponse) => {
        console.warn(
          `[WalletTypesService] failed to load wallet types, using local enum fallback:`,
          error.error?.message || error.message,
        );
        return of<IWalletTypeInfo[]>(this.fallbackWalletTypes);
      }),
      map(list => list.some(item => item.id === WalletTypeIdEnum.REAL)
        ? list
        : [this.realFallback, ...list]),
      shareReplay(1),
    );

  private readonly byId$: Observable<Map<WalletTypeIdEnum, IWalletTypeInfo>> = this.walletTypes$
    .pipe(
      map(list => new Map(list.map(item => [item.id, item]))),
      shareReplay(1),
    );

  private readonly byKey$: Observable<Map<WalletTypeEnum, IWalletTypeInfo>> = this.walletTypes$
    .pipe(
      map(list => new Map(list.map(item => [item.walletType, item]))),
      shareReplay(1),
    );

  constructor(private readonly http: HttpClient) {
  }

  getAll(): Observable<IWalletTypeInfo[]> {
    return this.walletTypes$;
  }

  getById(id: WalletTypeIdEnum | number): Observable<IWalletTypeInfo | undefined> {
    return this.byId$.pipe(map(map => map.get(id as WalletTypeIdEnum)));
  }

  getTitleById(id: WalletTypeIdEnum | number): Observable<string> {
    return this.byId$.pipe(map(map => map.get(id as WalletTypeIdEnum)?.title || WalletTypeIdEnum[id] || '-'));
  }

  getByKey(walletType: WalletTypeEnum): Observable<IWalletTypeInfo | undefined> {
    return this.byKey$.pipe(map(map => map.get(walletType)));
  }
}
